/**
 * Final submit — dry-run preconditions + execute RPC.
 * Both delegate to PL/pgSQL functions so the logic only lives in one place.
 */
import { supabase } from '@/lib/supabase/client'
import { ConflictError } from './errors'
import { parseTitle } from './internal'
import type {
  FinalSubmitCheck,
  OrphanDraft,
  DeprecatedSubmission,
  RecordStatus,
  ReviewMode,
} from './types'

/**
 * Read-only precondition check via `final_submit_record_check` RPC.
 * Returns: missing required forms + open decline comments + orphan drafts
 * (informational — orphan drafts do NOT block final submit).
 * Single source of truth; the execute RPC repeats the same checks under lock.
 */
export async function checkFinalSubmit(
  recordId: string,
  projectId: string,
): Promise<FinalSubmitCheck> {
  const { data, error } = await supabase.rpc('final_submit_record_check', {
    p_record_id: recordId,
    p_project_id: projectId,
  })
  if (error) throw error
  const result = data as {
    success?: boolean
    missing_forms?: unknown
    open_decline_comments?: number
    orphan_drafts?: unknown
    deprecated_submissions?: unknown
    total_required_forms?: number
    review_mode?: ReviewMode
    current_version?: number
    current_status?: RecordStatus
    error?: string
  }
  if (result?.error) throw new Error(result.error)

  const missingFormsRaw = Array.isArray(result.missing_forms) ? result.missing_forms : []
  const missingForms = missingFormsRaw.map((t) => parseTitle(t))
  const orphanRaw = Array.isArray(result.orphan_drafts) ? result.orphan_drafts : []
  const orphanDrafts: OrphanDraft[] = orphanRaw.map((d) => {
    const o = d as Record<string, unknown>
    return {
      formId: String(o.form_id ?? ''),
      formTitle: parseTitle(o.form_title),
      draftCount: Number(o.draft_count ?? 0),
      optional: !!o.optional,
    }
  })
  const depRaw = Array.isArray(result.deprecated_submissions) ? result.deprecated_submissions : []
  const deprecatedSubmissions: DeprecatedSubmission[] = depRaw.map((d) => {
    const o = d as Record<string, unknown>
    return {
      submissionId: String(o.submission_id ?? ''),
      formId: String(o.form_id ?? ''),
      formTitle: parseTitle(o.form_title),
      pinnedVersion: String(o.pinned_version ?? ''),
      instanceIndex: Number(o.instance_index ?? 1),
    }
  })
  return {
    success: !!result.success,
    missingForms,
    openDeclineComments: Number(result.open_decline_comments ?? 0),
    orphanDrafts,
    deprecatedSubmissions,
    totalRequiredForms: Number(result.total_required_forms ?? 0),
    reviewMode: (result.review_mode ?? 'required') as ReviewMode,
    currentVersion: Number(result.current_version ?? 0),
    currentStatus: (result.current_status ?? 'draft') as RecordStatus,
  }
}


/** Executes the final submit transition. Call only after preconditions pass. */
export async function finalSubmitRecord(
  recordId: string,
  projectId: string,
  userId: string,
  userRole: string,
): Promise<void> {
  const { data: rec, error: recErr } = await supabase
    .from('records')
    .select('version')
    .eq('id', recordId)
    .single()
  if (recErr) throw recErr

  const { data, error } = await supabase.rpc('final_submit_record', {
    p_record_id: recordId,
    p_user_id: userId,
    p_project_id: projectId,
    p_expected_version: rec.version,
  })
  if (error) throw error

  const result = data as {
    success?: boolean
    error?: string
    current_version?: number
    current_status?: string
    missing_forms?: string[]
  }

  if (result?.error === 'conflict') {
    throw new ConflictError(result.current_version ?? 0, result.current_status ?? 'unknown')
  }
  if (result?.error === 'missing_forms') {
    throw new Error(`فرم‌های الزامی ارسال نشده: ${(result.missing_forms ?? []).join('، ')}`)
  }
  if (result?.error === 'open_decline_comments') {
    throw new Error('نظرات رد حل‌نشده وجود دارد')
  }
  if (result?.error === 'deprecated_versions') {
    throw new Error('برخی فرم‌ها روی نسخه قدیمی پر شده‌اند. ابتدا آن‌ها را به‌روزرسانی کنید.')
  }
  if (result?.error) {
    throw new Error(result.error)
  }

  // Notifications are emitted server-side by the records status-change
  // trigger (migration 20260718120000) — nothing to send from here.
  void userRole
}
